/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-10-16 11:21:53
 **/
/* eslint-disable @typescript-eslint/no-unused-vars */

import { produce } from "immer";
import { usePersistStore } from "@/frameworks/core/persistence";
import type { GroupDispatch } from "./states";
import { PeopleService } from "@/frameworks/core/API";

const LOGTAG = "#Group:Actions#";

/**
 * Enum representing group cache options.
 * @enum {string}
 */
export enum GroupCacheOption {
	LIST = "LIST"
}

export default class GroupActions implements GroupDispatch {
	private static _instance: GroupActions | null = null;
	private constructor() {}
	public static get instance() {
		if (!GroupActions._instance) {
			GroupActions._instance = new GroupActions();
		}
		return GroupActions._instance;
	}

	/**
	 * Sets the groups data within the application's persistent store.
	 * Updates the state with the provided groups data.
	 *
	 * @param {WebServices.People.List.Data} groups - The data structure containing the groups information to be set in the state.
	 * @return {void}
	 */
	public set_groups(groups: WebServices.People.List.Data): void {
		usePersistStore.setState((state) =>
			produce(state, (draft) => {
				draft.groups = groups;
			})
		);
	}

	/**
	 * Refresh the group cache based on the specified method.
	 * @param {GroupCacheOption} method - The cache method option.
	 * @param {Function} [callbackAfterForceRefresh] - Optional callback to be called after a force refresh.
	 */
	public async refreshGroupCache(method: GroupCacheOption, callbackAfterForceRefresh?: () => void) {
		if (method === GroupCacheOption.LIST) {
			// Call the API to get groups and set the response to GroupActions
			const peopleAPI = new PeopleService({});
			const { data } = await peopleAPI.people.list.execute().catch(() => {
				return { data: null };
			});
			if (data) {
				GroupActions.instance.set_groups(data);
				if (callbackAfterForceRefresh) {
					callbackAfterForceRefresh();
				}
			}
		}
	}
}
